/**
 * 初始化
 */
export const INIT_SIGNAL = 'NOTIFY_BROADCAST_CHANNEL_INIT' as const;
/**
 * SSE 连接死亡
 */
export const DEAD_SIGNAL = 'NOTIFY_SSE_RECEIVER_DEAD' as const;
/**
 * 排除和被排除
 */
export const EXCLUDE_SIGNAL = 'NOTIFY_BROADCAST_CHANNEL_EXCLUDE' as const;
export const NOTIFY_SIGNAL_LIST = new Set([INIT_SIGNAL, EXCLUDE_SIGNAL, DEAD_SIGNAL]);

/**
 * Notify SDK 华龙芯推送消息的业务类型
 *
 * - SYSTEM: 系统消息
 * - BUSINESS: 业务消息
 */
export type XSdkNotifyBusinessType = 'SYSTEM' | 'BUSINESS';

/**
 * Notify SDK 华龙芯推送消息的动作类型
 *
 * - DISPLAY: 此类型消息前端应该显示或与用户做交互
 * - SILENCE: 此类型消息前端应该用户无感知的实现业务
 */
export type XSdkNotifyType = 'DISPLAY' | 'SILENCE';

/**
 * Notify SDK 华龙芯推送消息的动作类型
 *
 * - INTERFACE: SSE 接口返回的消息
 * - BROADCAST: 此窗口接收到 BroadcastChannel 本地广播的消息
 */
export type XSdkNotifySource = 'INTERFACE' | 'BROADCAST';

/**
 * Notify SDK SSE链接返回的消息类型
 */
export type XSdkNotifyRemoteData = {
  /**
   * 华龙芯推送消息事件名称 - 对应后端的枚举值
   */
  eventTypeCode: string;
  /**
   * 华龙芯推送消息事件名称 - 枚举值的汉字名称
   */
  eventName: string;
  /**
   * 华龙芯推送消息的业务类型 SYSTEM - BUSINESS
   */
  eventBusinessType: XSdkNotifyBusinessType;
  /**
   * 华龙芯推送消息的动作类型
   */
  eventNotifyType: XSdkNotifyType;
  /**
   * 华龙芯推送消息的实际数据
   */
  eventContent: string;
  /**
   * 消息通知、未读消息数量
   */
  unConfirmCount: number;
  /**
   * 强制推送
   */
  forcePublish: boolean;
  /**
   * 消息id
   */
  eventUserMessageId: string;
};

/**
 * Notify SDK 广播消息类型，其中 eventSource 字段会被强制指定为 'BROADCAST'
 */
export interface XSdkNotifyBroadcastData extends XSdkNotifyRemoteData {
  /**
   * 华龙芯推送消息的id
   */
  id: string;
  /**
   * 区分消息是接口返回还是本地广播的
   */
  eventSource: Exclude<XSdkNotifySource, 'INTERFACE'>;
  /**
   * 华龙芯推送消息的原始数据
   */
  eventOriginData: any;
}

/**
 * Notify SDK 事件的数据类型，业务测统一接受这种数据类型
 */
export interface XSdkNotifyData extends Omit<XSdkNotifyBroadcastData, 'eventSource'> {
  /**
   * 区分消息是接口返回还是本地广播的
   */
  eventSource: XSdkNotifySource;
}

/**
 * Notify SDK 实例化参数
 */
export type XSdkNotifyConstructorOptions = {
  /**
   * SSE 接口服务地址（协议相对地址，如 `//hlxapi.cqnews.net`）。
   *
   * 支持协议相对 URL 字符串与按域名映射的 JSON 字符串两种格式，由 `getBaseConfigByOrigin` 归一化。
   *
   * @defaultValue 未指定时回退到当前站点同域地址
   */
  apiBase?: string;
  token: () => string;
  freshToken: () => Promise<void>;
  /**
   * 使用此参数可以过滤掉不需要的事件，如果是 function 时，返回值为true时生效
   *
   * @defaultValue ['HEARTBEAT']
   */
  eventFilter?: Array<string> | ((str: string) => boolean);
};

/**
 * Notify SDK 私有信号数据
 */
export type XSdkNotifyPrivateData = {
  /**
   * 只有竞争收发实例的时候会发送uid
   */
  uid?: string;
  type: typeof INIT_SIGNAL | typeof DEAD_SIGNAL | typeof EXCLUDE_SIGNAL;
  data: XSdkNotifyData;
};

/**
 * Notify SDK 客户端发送消息的前缀
 */
export const CLIENT_SIGNAL_PREFIX_SYMBOL = 'CLIENT_' as const;

/**
 * Notify SDK 客户端发送消息的前缀类型
 */
export type XSdkNotifyClientSignalPrefixSymbol = typeof CLIENT_SIGNAL_PREFIX_SYMBOL;

/**
 * Notify SDK 客户端发送消息信号标识
 */
export type XSdkNotifyClientSignal = `${XSdkNotifyClientSignalPrefixSymbol}${string}`;

/**
 * 由客户端手动发送的消息，一般是开发者的业务实现信号
 */
export type XSdkNotifyClientData = {
  type: XSdkNotifyClientSignal;
  data?: any;
};

export interface XSdkNotifyEventSourceEventMap {
  error: ErrorEvent;
  open: Event;
}
